import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { ShieldCheck, Trash2, KeyRound } from "lucide-react";
import "@/Gate.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminPanel = () => {
  const [authenticated, setAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [adminPassword, setAdminPassword] = useState(sessionStorage.getItem("admin_password") || "");
  const [newCode, setNewCode] = useState("");
  const [requests, setRequests] = useState([]);

  const authHeaders = (pwd) => ({ headers: { "X-Admin-Password": pwd } });

  useEffect(() => {
    if (adminPassword) {
      tryLogin(adminPassword);
    }
  }, []);

  const tryLogin = async (pwd) => {
    try {
      await axios.post(`${API}/admin/login`, {}, authHeaders(pwd));
      sessionStorage.setItem("admin_password", pwd);
      setAdminPassword(pwd);
      setAuthenticated(true);
      fetchData(pwd);
    } catch (error) {
      sessionStorage.removeItem("admin_password");
      setAuthenticated(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/login`, {}, authHeaders(password));
      sessionStorage.setItem("admin_password", password);
      setAdminPassword(password);
      setAuthenticated(true);
      fetchData(password);
      toast.success("Connexion réussie");
    } catch (error) {
      toast.error("Mot de passe administrateur incorrect");
    }
  };

  const fetchData = async (pwd) => {
    try {
      const [codeRes, requestsRes] = await Promise.all([
        axios.get(`${API}/admin/access-code`, authHeaders(pwd)),
        axios.get(`${API}/admin/access-requests`, authHeaders(pwd)),
      ]);
      setNewCode(codeRes.data.code);
      setRequests(requestsRes.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des données");
    }
  };

  const handleUpdateCode = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/admin/access-code`, { code: newCode }, authHeaders(adminPassword));
      toast.success("Code d'accès mis à jour");
    } catch (error) {
      toast.error("Erreur lors de la mise à jour du code");
    }
  };

  const handleDeleteRequest = async (id) => {
    try {
      await axios.delete(`${API}/admin/access-requests/${id}`, authHeaders(adminPassword));
      setRequests(requests.filter((r) => r.id !== id));
      toast.success("Demande supprimée");
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  if (!authenticated) {
    return (
      <div className="gate-container">
        <Toaster position="top-right" />
        <div className="gate-card" data-testid="admin-login-card">
          <div className="gate-icon-wrapper">
            <ShieldCheck className="gate-icon" />
          </div>
          <h1 className="admin-title">Espace administrateur</h1>
          <form onSubmit={handleLogin} className="gate-form">
            <Input
              data-testid="admin-password-input"
              type="password"
              placeholder="Mot de passe administrateur"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoFocus
            />
            <Button data-testid="admin-login-button" type="submit" className="btn-primary gate-submit-btn">
              Se connecter
            </Button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="app-container">
      <Toaster position="top-right" />
      <div className="admin-wrapper">
        <h1 className="admin-title" data-testid="admin-panel-title">Espace administrateur</h1>

        <div className="admin-section">
          <h2><KeyRound className="admin-section-icon" /> Code d'accès</h2>
          <form onSubmit={handleUpdateCode} className="admin-code-form">
            <div className="form-field">
              <Label htmlFor="access-code">Code actuel</Label>
              <Input
                data-testid="admin-access-code-input"
                id="access-code"
                value={newCode}
                onChange={(e) => setNewCode(e.target.value)}
                required
              />
            </div>
            <Button data-testid="admin-update-code-button" type="submit" className="btn-primary">
              Mettre à jour
            </Button>
          </form>
        </div>

        <div className="admin-section">
          <h2>Demandes d'accès ({requests.length})</h2>
          <div className="table-container">
            <Table>
              <TableHeader>
                <TableRow className="table-header-row">
                  <TableHead>Nom</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Message</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {requests.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center empty-state">
                      Aucune demande d'accès pour le moment
                    </TableCell>
                  </TableRow>
                ) : (
                  requests.map((r) => (
                    <TableRow key={r.id} className="table-row" data-testid={`access-request-row-${r.id}`}>
                      <TableCell className="font-medium">{r.nom}</TableCell>
                      <TableCell>{r.email}</TableCell>
                      <TableCell className="max-w-xs truncate">{r.message || "-"}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          data-testid={`delete-access-request-button-${r.id}`}
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteRequest(r.id)}
                          className="action-btn delete-btn"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
    </div>
  );
};
